import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Save, RotateCcw, ArrowLeft, Sparkles, Sliders,
  Calculator, Plus, Trash2, Info,
} from "lucide-react";
import { lmsGetGradingCriteria, lmsSaveGradingCriteria } from "../api/lmsApi";
import "../lms.css";

/* ─────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────── */
const getSchoolId = () => {
  try {
    const u = JSON.parse(localStorage.getItem("userData") || "{}");
    return u.school_id || u.admin_id || null;
  } catch { return null; }
};

const DEFAULT_TIERS = [
  { grade_name: "Outstanding",       min_percentage: 100,  max_percentage: 100,   color_code: "#10b981", remarks: "Flawless Performance",          sort_order: 1  },
  { grade_name: "Excellent",         min_percentage: 90,   max_percentage: 99.99, color_code: "#059669", remarks: "Mastery of Subject",             sort_order: 2  },
  { grade_name: "Very Good",         min_percentage: 80,   max_percentage: 89.99, color_code: "#2563eb", remarks: "Strong Understanding",            sort_order: 3  },
  { grade_name: "Good",              min_percentage: 70,   max_percentage: 79.99, color_code: "#3b82f6", remarks: "Good Comprehension",              sort_order: 4  },
  { grade_name: "Above Average",     min_percentage: 60,   max_percentage: 69.99, color_code: "#6366f1", remarks: "Above Expected Level",            sort_order: 5  },
  { grade_name: "Average",           min_percentage: 50,   max_percentage: 59.99, color_code: "#8b5cf6", remarks: "Meets Basic Requirements",        sort_order: 6  },
  { grade_name: "Below Average",     min_percentage: 40,   max_percentage: 49.99, color_code: "#f59e0b", remarks: "Below Expected Level",            sort_order: 7  },
  { grade_name: "Satisfactory",      min_percentage: 35,   max_percentage: 39.99, color_code: "#f97316", remarks: "Minimum Pass Criteria",           sort_order: 8  },
  { grade_name: "Needs Improvement", min_percentage: 20,   max_percentage: 34.99, color_code: "#ef4444", remarks: "Requires Additional Focus",       sort_order: 9  },
  { grade_name: "Poor",              min_percentage: 0,    max_percentage: 19.99, color_code: "#dc2626", remarks: "Significant Support Needed",      sort_order: 10 },
];

/* ─────────────────────────────────────────────────────────
   Main Page
───────────────────────────────────────────────────────── */
export default function GradeConfiguration() {
  const navigate  = useNavigate();
  const schoolId  = getSchoolId();

  const [criteria,  setCriteria]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [testScore, setTestScore] = useState(85);

  /* ── fetch ── */
  const loadCriteria = async () => {
    if (!schoolId) { setCriteria(DEFAULT_TIERS); setLoading(false); return; }
    setLoading(true);
    try {
      const res  = await lmsGetGradingCriteria(schoolId);
      const data = res?.data?.data || [];
      if (data.length > 0) { setCriteria(data); setIsDefault(Boolean(res?.data?.is_default)); }
      else                  { setCriteria(DEFAULT_TIERS); setIsDefault(true); }
    } catch { setCriteria(DEFAULT_TIERS); setIsDefault(true); }
    finally  { setLoading(false); }
  };

  useEffect(() => { loadCriteria(); }, [schoolId]);

  /* ── mutations ── */
  const handleAddTier = () => {
    const n = criteria.length + 1;
    setCriteria([...criteria, { grade_name: `Grade ${n}`, min_percentage: 0, max_percentage: 50, color_code: "#6366f1", remarks: "New criteria tier", sort_order: n }]);
  };

  const handleRemoveTier = (i) => {
    if (criteria.length <= 1) { toast.error("At least one grade tier is required."); return; }
    setCriteria(criteria.filter((_, idx) => idx !== i));
  };

  const handleChange = (i, field, value) => {
    const updated = [...criteria];
    updated[i] = { ...updated[i], [field]: value };
    setCriteria(updated);
  };

  const handleReset = () => { setCriteria(DEFAULT_TIERS); toast.info("Reset to standard grading criteria."); };

  const handleSave = async () => {
    if (!schoolId) { toast.error("School ID not found. Please log in again."); return; }
    for (let i = 0; i < criteria.length; i++) {
      const { grade_name, min_percentage, max_percentage } = criteria[i];
      if (!grade_name?.trim())                               { toast.error(`Grade Name required for Tier #${i + 1}`); return; }
      if (isNaN(+min_percentage) || isNaN(+max_percentage)) { toast.error(`Invalid % range for ${grade_name}`); return; }
      if (+min_percentage > +max_percentage)                 { toast.error(`Min % > Max % for ${grade_name}`); return; }
    }
    setSaving(true);
    try {
      await lmsSaveGradingCriteria({ school_id: schoolId, criteria });
      toast.success("Grading criteria saved!");
      setIsDefault(false);
      loadCriteria();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save grading criteria");
    } finally { setSaving(false); }
  };

  /* ── live tester ── */
  const evaluatedGrade = (() => {
    const v = parseFloat(testScore) || 0;
    for (const item of criteria) {
      if (v >= (parseFloat(item.min_percentage) || 0) && v <= (parseFloat(item.max_percentage) || 100) + 0.001)
        return item;
    }
    return criteria[criteria.length - 1] || null;
  })();

  /* ── colour helper for tester slider track ── */
  const sliderPct = testScore;

  return (
    <div style={{ width: "100%", boxSizing: "border-box" }}>

      {/* ══════════════════════════════
          PAGE HEADER
      ══════════════════════════════ */}
      <div style={{
        background: "#fff",
        borderRadius: 16,
        border: "1px solid #e2e8f0",
        boxShadow: "0 1px 6px rgba(15,23,42,.05)",
        padding: "18px 24px",
        marginBottom: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        flexWrap: "wrap",
      }}>
        {/* left */}
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button
            onClick={() => navigate("/lms/exams")}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "8px 14px", borderRadius: 9,
              border: "1.5px solid #e2e8f0", background: "#f8fafc",
              color: "#475569", fontSize: 13, fontWeight: 700, cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            <ArrowLeft size={15} /> Exams
          </button>

          <div style={{ borderLeft: "1px solid #e2e8f0", paddingLeft: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
                Auto Grade Configuration
              </h1>
              {isDefault && (
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
                  background: "#fef9c3", color: "#854d0e", border: "1px solid #fde68a",
                }}>
                  Using Defaults
                </span>
              )}
            </div>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b", lineHeight: 1.4 }}>
              Configure automatic grading used for performance sheets, certificates &amp; student evaluation.
            </p>
          </div>
        </div>

        {/* actions */}
        <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
          <button
            onClick={handleReset}
            style={{
              display: "flex", alignItems: "center", gap: 7, padding: "9px 18px",
              borderRadius: 9, border: "1.5px solid #cbd5e1", background: "#fff",
              color: "#334155", fontSize: 13, fontWeight: 700, cursor: "pointer",
              transition: "background .15s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
            onMouseLeave={e => e.currentTarget.style.background = "#fff"}
          >
            <RotateCcw size={14} /> Reset Defaults
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="lms-btn-primary"
            style={{
              display: "flex", alignItems: "center", gap: 7, padding: "9px 22px",
              borderRadius: 9, fontSize: 13, fontWeight: 700,
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.75 : 1,
            }}
          >
            <Save size={15} /> {saving ? "Saving…" : "Save Criteria"}
          </button>
        </div>
      </div>

      {/* ══════════════════════════════
          BODY  (brackets + sidebar)
      ══════════════════════════════ */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 300px",
        gap: 20,
        alignItems: "start",
      }}>

        {/* ── LEFT : Grading Brackets ── */}
        <div style={{
          background: "#fff", borderRadius: 16,
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(15,23,42,.05)",
          overflow: "hidden",
        }}>
          {/* panel header */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 20px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <div style={{
                width: 34, height: 34, borderRadius: 10, background: "#eff6ff",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Sliders size={16} color="#2563eb" />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#1e293b" }}>Grading Brackets</div>
                <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>{criteria.length} tiers configured</div>
              </div>
            </div>
            <button
              onClick={handleAddTier}
              style={{
                display: "flex", alignItems: "center", gap: 6, padding: "8px 15px",
                borderRadius: 9, border: "1.5px solid #bfdbfe", background: "#eff6ff",
                color: "#1d4ed8", fontSize: 12, fontWeight: 700, cursor: "pointer",
              }}
            >
              <Plus size={13} /> Add Grade Tier
            </button>
          </div>

          {/* tier list */}
          {loading ? (
            <div style={{ padding: 60, textAlign: "center", color: "#94a3b8", fontSize: 14 }}>
              Loading grading criteria…
            </div>
          ) : (
            <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
              {criteria.map((tier, idx) => (
                <TierCard
                  key={idx}
                  tier={tier}
                  index={idx}
                  onChange={(field, val) => handleChange(idx, field, val)}
                  onRemove={() => handleRemoveTier(idx)}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── RIGHT : Sidebar ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Live Grade Tester */}
          <div style={{
            background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0",
            boxShadow: "0 1px 6px rgba(15,23,42,.05)", overflow: "hidden",
          }}>
            {/* card header */}
            <div style={{
              padding: "14px 18px", background: "#f0fdf4", borderBottom: "1px solid #d1fae5",
              display: "flex", alignItems: "center", gap: 9,
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: 9, background: "#dcfce7",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Calculator size={15} color="#16a34a" />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#14532d" }}>Live Grade Tester</div>
                <div style={{ fontSize: 11, color: "#4ade80", fontWeight: 600 }}>Real-time preview</div>
              </div>
            </div>

            <div style={{ padding: "16px 18px" }}>
              <p style={{ margin: "0 0 16px", fontSize: 12, color: "#64748b", lineHeight: 1.55 }}>
                Drag the slider to see how your grading brackets evaluate any student percentage.
              </p>

              {/* slider */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>Test Percentage</span>
                  <span style={{
                    fontSize: 15, fontWeight: 900, color: "#fff",
                    background: "#2563eb", padding: "2px 10px", borderRadius: 20, minWidth: 48, textAlign: "center",
                  }}>
                    {testScore}%
                  </span>
                </div>
                <div style={{ position: "relative" }}>
                  <input
                    type="range" min="0" max="100" value={testScore}
                    onChange={e => setTestScore(Number(e.target.value))}
                    style={{
                      width: "100%", height: 6, borderRadius: 99,
                      accentColor: evaluatedGrade?.color_code || "#2563eb",
                      cursor: "pointer", display: "block",
                    }}
                  />
                  <div style={{
                    display: "flex", justifyContent: "space-between",
                    fontSize: 10, color: "#cbd5e1", fontWeight: 700, marginTop: 4,
                  }}>
                    <span>0%</span><span>50%</span><span>100%</span>
                  </div>
                </div>
              </div>

              {/* result box */}
              {evaluatedGrade && (
                <div style={{
                  borderRadius: 12,
                  background: `${evaluatedGrade.color_code}15`,
                  border: `2px solid ${evaluatedGrade.color_code}40`,
                  padding: "16px",
                  textAlign: "center",
                }}>
                  <div style={{
                    fontSize: 10, fontWeight: 800, color: "#94a3b8",
                    textTransform: "uppercase", letterSpacing: 1, marginBottom: 6,
                  }}>
                    Evaluated Result
                  </div>
                  <div style={{
                    width: 14, height: 14, borderRadius: "50%",
                    background: evaluatedGrade.color_code,
                    margin: "0 auto 8px",
                    boxShadow: `0 0 0 4px ${evaluatedGrade.color_code}30`,
                  }} />
                  <div style={{ fontSize: 22, fontWeight: 900, color: evaluatedGrade.color_code, lineHeight: 1.1 }}>
                    {evaluatedGrade.grade_name}
                  </div>
                  <div style={{ fontSize: 12, color: "#475569", marginTop: 5, fontWeight: 600 }}>
                    {evaluatedGrade.remarks}
                  </div>
                  <div style={{
                    display: "inline-block", marginTop: 10,
                    fontSize: 11, fontWeight: 700, color: "#64748b",
                    padding: "4px 12px", background: "#fff",
                    borderRadius: 20, border: "1px solid #e2e8f0",
                  }}>
                    {evaluatedGrade.min_percentage}% – {evaluatedGrade.max_percentage}%
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Grade Summary Pills */}
          <div style={{
            background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0",
            boxShadow: "0 1px 6px rgba(15,23,42,.05)", padding: "16px 18px",
          }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#1e293b", marginBottom: 12 }}>
              Grade Overview
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {criteria.map((t, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "7px 12px", borderRadius: 9,
                  background: testScore >= (parseFloat(t.min_percentage)||0) && testScore <= (parseFloat(t.max_percentage)||100) + 0.001
                    ? `${t.color_code}15` : "#f8fafc",
                  border: testScore >= (parseFloat(t.min_percentage)||0) && testScore <= (parseFloat(t.max_percentage)||100) + 0.001
                    ? `1.5px solid ${t.color_code}50` : "1.5px solid transparent",
                  transition: "all .2s",
                }}>
                  <div style={{
                    width: 10, height: 10, borderRadius: "50%",
                    background: t.color_code, flexShrink: 0,
                  }} />
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: "#334155" }}>
                    {t.grade_name}
                  </span>
                  <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>
                    {t.min_percentage}–{t.max_percentage}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Info card */}
          <div style={{
            borderRadius: 16, border: "1px solid #fde68a",
            background: "#fffbeb", padding: "16px 18px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
              <Sparkles size={15} color="#d97706" />
              <span style={{ fontSize: 13, fontWeight: 800, color: "#92400e" }}>Auto System Evaluation</span>
            </div>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: "#78350f", lineHeight: 1.85 }}>
              <li><strong>Student Grade</strong> — computed after exam submission.</li>
              <li><strong>Certificate</strong> — auto-issued with the assigned grade.</li>
              <li><strong>Weak Chapters</strong> — flagged if accuracy &lt; 50%.</li>
              <li><strong>Practice Links</strong> — mapped from LMS library.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Tier Card
───────────────────────────────────────────────────────── */
function TierCard({ tier, index, onChange, onRemove }) {
  return (
    <div style={{
      border: "1.5px solid #e2e8f0",
      borderRadius: 12,
      background: "#fafbfc",
      transition: "box-shadow .15s",
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 12px rgba(37,99,235,.07)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
    >
      {/* ── Row 1 ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10, padding: "12px 14px 8px",
        flexWrap: "wrap",
      }}>
        {/* color swatch */}
        <div style={{ position: "relative", flexShrink: 0 }}>
          <div style={{
            width: 30, height: 30, borderRadius: "50%",
            background: tier.color_code || "#2563eb",
            border: "2.5px solid #fff",
            boxShadow: `0 0 0 2px ${tier.color_code || "#2563eb"}60`,
            overflow: "hidden", cursor: "pointer",
          }}>
            <input
              type="color"
              value={tier.color_code || "#2563eb"}
              onChange={e => onChange("color_code", e.target.value)}
              style={{ opacity: 0, position: "absolute", inset: 0, width: "100%", height: "100%", cursor: "pointer", border: "none" }}
              title="Pick Grade Color"
            />
          </div>
        </div>

        {/* index */}
        <span style={{
          fontSize: 11, fontWeight: 800, color: "#cbd5e1",
          flexShrink: 0, minWidth: 22,
        }}>
          #{index + 1}
        </span>

        {/* grade name */}
        <div style={{ flex: "1 1 150px" }}>
          <label style={labelStyle}>Grade Name</label>
          <input
            type="text"
            value={tier.grade_name}
            onChange={e => onChange("grade_name", e.target.value)}
            placeholder="e.g. Outstanding"
            style={inputStyle}
          />
        </div>

        {/* min */}
        <div style={{ width: 85, flexShrink: 0 }}>
          <label style={labelStyle}>Min %</label>
          <input
            type="number" min="0" max="100" step="0.01"
            value={tier.min_percentage}
            onChange={e => onChange("min_percentage", e.target.value)}
            style={{ ...inputStyle, textAlign: "center" }}
          />
        </div>

        <span style={{ color: "#cbd5e1", fontSize: 18, fontWeight: 300, marginTop: 16, flexShrink: 0 }}>—</span>

        {/* max */}
        <div style={{ width: 85, flexShrink: 0 }}>
          <label style={labelStyle}>Max %</label>
          <input
            type="number" min="0" max="100" step="0.01"
            value={tier.max_percentage}
            onChange={e => onChange("max_percentage", e.target.value)}
            style={{ ...inputStyle, textAlign: "center" }}
          />
        </div>
      </div>

      {/* ── Row 2 ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "0 14px 12px", borderTop: "1px dashed #f1f5f9", paddingTop: 8,
      }}>
        <div style={{ flex: 1 }}>
          <label style={labelStyle}>Evaluation Remarks</label>
          <input
            type="text"
            value={tier.remarks || ""}
            onChange={e => onChange("remarks", e.target.value)}
            placeholder="e.g. Excellent grasp of subject matter"
            style={{ ...inputStyle, fontWeight: 500, color: "#475569" }}
          />
        </div>
        <button
          onClick={onRemove}
          title="Remove Tier"
          style={{
            width: 34, height: 34, borderRadius: 8, flexShrink: 0,
            border: "1.5px solid #fecaca", background: "#fff5f5",
            color: "#dc2626", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            marginTop: 18, transition: "background .15s",
          }}
          onMouseEnter={e => e.currentTarget.style.background = "#fee2e2"}
          onMouseLeave={e => e.currentTarget.style.background = "#fff5f5"}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

/* ── shared micro-styles ── */
const labelStyle = {
  display: "block",
  fontSize: 10,
  fontWeight: 700,
  color: "#94a3b8",
  marginBottom: 4,
  textTransform: "uppercase",
  letterSpacing: 0.5,
};

const inputStyle = {
  width: "100%",
  padding: "7px 11px",
  borderRadius: 8,
  border: "1.5px solid #e2e8f0",
  fontSize: 13,
  fontWeight: 700,
  color: "#1e293b",
  outline: "none",
  boxSizing: "border-box",
  background: "#fff",
  transition: "border-color .15s",
};
